import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'

import {
  ALL_VEHICLES_SELECT_QUERY,
  type AllVehiclesSelectQuery,
  type AllVehiclesQueryVariables,
  type VehicleSelectNode,
} from '@/modules/vehicles'
import { Combobox, type ComboboxItem } from '../combobox'
import { type VehicleSelectProps } from './types'

const VehicleSelect = (allProps: VehicleSelectProps) => {
  const {
    currentlyOwned = true,
    excludeVehicleIds = [],
    placeholder,
    searchPlaceholder,
    noResultsText,
    ...comboboxProps
  } = allProps

  const translations = useTranslate({
    loading: 'components.combobox.vehicle.loading',
    error: 'components.combobox.vehicle.error',
    single: {
      placeholder: 'components.combobox.vehicle.placeholder',
      searchPlaceholder: 'components.combobox.vehicle.search-placeholder',
      noResults: 'components.combobox.vehicle.no-results',
    },
    multiple: {
      placeholder: 'components.multi-combobox.vehicle.placeholder',
      searchPlaceholder: 'components.multi-combobox.vehicle.search-placeholder',
      noResults: 'components.multi-combobox.vehicle.no-results',
    },
  })

  const { data, loading, error } = useQuery<
    AllVehiclesSelectQuery,
    AllVehiclesQueryVariables
  >(ALL_VEHICLES_SELECT_QUERY, {
    variables: {
      orderBy: 'registrationNumber',
      currentlyOwned,
    },
  })

  const excludedIdSet = useMemo(
    () => new Set(excludeVehicleIds),
    [excludeVehicleIds]
  )

  const vehicles = useMemo<VehicleSelectNode[]>(
    () => (data?.allVehicles.edges ?? []).map(({ node }) => node),
    [data?.allVehicles.edges]
  )

  const filteredVehicles = useMemo(() => {
    // Exclusions are applied to both displayed items and returned selections.
    if (excludedIdSet.size === 0) return vehicles
    return vehicles.filter(vehicle => !excludedIdSet.has(vehicle.id))
  }, [vehicles, excludedIdSet])

  // Enables O(1) lookup and preserves selected-id order in multi-select mapping.
  const vehiclesById = useMemo(
    () => new Map(filteredVehicles.map(vehicle => [vehicle.id, vehicle])),
    [filteredVehicles]
  )

  const items = useMemo<ComboboxItem<string>[]>(
    () =>
      filteredVehicles.map(vehicle => ({
        value: vehicle.id,
        // A dummy vehicle has no plate, so it is identified by its name.
        label:
          vehicle.registrationNumber ||
          vehicle.name ||
          vehicle.vin ||
          vehicle.id,
        // Allow searching by registration number, name and VIN.
        filterValue: [vehicle.registrationNumber, vehicle.name, vehicle.vin]
          .filter(Boolean)
          .join(' '),
      })),
    [filteredVehicles]
  )

  // Loading/error states replace the empty-list text so the dropdown never
  // reads as "no vehicles" while the query is still in flight or has failed.
  const emptyText = loading
    ? translations.loading
    : error
    ? translations.error
    : null

  if (comboboxProps.mode === 'multiple') {
    return (
      <Combobox
        {...comboboxProps}
        mode="multiple"
        items={items}
        placeholder={placeholder ?? translations.multiple.placeholder}
        searchPlaceholder={
          searchPlaceholder ?? translations.multiple.searchPlaceholder
        }
        noResultsText={
          emptyText ?? noResultsText ?? translations.multiple.noResults
        }
        onChange={ids =>
          comboboxProps.onChange(
            ids
              .map(id => vehiclesById.get(id))
              .filter((vehicle): vehicle is VehicleSelectNode => !!vehicle)
          )
        }
      />
    )
  }

  return (
    <Combobox
      {...comboboxProps}
      mode="single"
      items={items}
      placeholder={placeholder ?? translations.single.placeholder}
      searchPlaceholder={
        searchPlaceholder ?? translations.single.searchPlaceholder
      }
      noResultsText={
        emptyText ?? noResultsText ?? translations.single.noResults
      }
      onChange={id => comboboxProps.onChange(vehiclesById.get(id) ?? null)}
    />
  )
}

export default VehicleSelect
